import React, { useState, useEffect } from "react";
import { produce } from "immer";
import RoleModelingBox from "../../role-modeling-box";
import {
    openRoleMenuNode,
    addRoleMenuFolder,
    addRoleMenuNode,
    getTransactionsByIds,
    removeRoleMenuNode,
    copyRoleMenu,
    getPfcgTreeForTcodes,
    getDefaultPermissionsValuesForTransactions,
    getTcodesMapCountFromMenu,
    getDeletedTcodesFromMenu,
} from "../../../../utils/role-menu";
import {
    addItemToRoleTree,
    modifyItemFieldInRoleTree,
    removeItemFromRoleTree,
    updateRoleTreeOrgLevels,
    getDeletedDefaultValuesForTcodes,
    substractPfcgTreeFromRole,
} from "../../../../utils/reports-modeling-utils.ts";
import { findItemInTree, TreeWalker, findFieldInTree } from "../../../../utils/tree-table.js";
import PropTypes from "prop-types";

const SingleRoleView = ({ form, setForm, setBusy, disabled }) => {
    const [searchString, setSearchString] = useState("");
    useEffect(() => {
        if (form?.tree?.length) {
            setForm((prevForm) => {
                const updatedTree = produce(prevForm.tree, (draftTree) => {
                    findFieldInTree(draftTree, searchString);
                });

                return {
                    ...prevForm,
                    tree: updatedTree,
                };
            });
        }
    }, [searchString, setForm, form.tree.length]);

    useEffect(() => {
        setSearchString("");
    }, [form?.id]);

    const handleAddTreeItems = (items) => {
        const treeChanges = produce(
            { tree: form.tree, orgLevels: form.orgLevels, delta: form.delta, orgLevelsDelta: form.orgLevelsDelta },
            ({ tree, delta, orgLevels, orgLevelsDelta }) => {
                for (const item of items) addItemToRoleTree(form.profile, orgLevels, tree, item, null, 0, [], delta, orgLevelsDelta, {}, true);
            }
        );
        setForm({ ...form, tree: treeChanges.tree, orgLevels: treeChanges.orgLevels, delta: treeChanges.delta, orgLevelsDelta: treeChanges.orgLevelsDelta });
    };

    const handleSetOrgLevelValues = (values) => {
        const changes = produce({ orgLevels: form.orgLevels, orgLevelsDelta: form.orgLevelsDelta, tree: form.tree }, ({ orgLevels, orgLevelsDelta, tree }) =>
            updateRoleTreeOrgLevels(orgLevels, values, orgLevelsDelta, tree)
        );
        setForm({ ...form, orgLevels: changes.orgLevels, orgLevelsDelta: changes.orgLevelsDelta, tree: changes.tree, status: changes.status });
    };

    const handleTreeItemFieldChange = (itemPath, field, value) => {
        const changes = produce({ tree: form.tree, delta: form.delta, orgLevels: form.orgLevels }, ({ tree, delta, orgLevels }) =>
            modifyItemFieldInRoleTree(orgLevels, tree, itemPath, field, value, delta)
        );

        setForm({ ...form, tree: changes.tree, delta: changes.delta, orgLevels: changes.orgLevels, status: changes.status });
    };

    const handleOpenRoleMenuNode = (itemPath) => {
        const changes = produce({ menu: form.menu }, ({ menu }) => {
            openRoleMenuNode(itemPath, menu);
        });
        setForm({ ...form, menu: changes.menu });
    };

    const handleAddRoleMenuFolder = (activeNodePath, folderName) => {
        const changes = produce({ menu: form.menu }, ({ menu }) => {
            addRoleMenuFolder(activeNodePath, menu, folderName);
        });
        setForm({ ...form, menu: changes.menu });
    };

    const handleAddRoleMenuNodeMass = async (activeNodePath, tcodes) => {
        setBusy(true);

        await Promise.all(
            tcodes.map(async (tcode) => {
                await handleAddRoleMenuNode(activeNodePath, [tcode]);
            })
        );

        setBusy(false);
    };

    const handleAddRoleMenuNode = async (activeNodePath, tcodes) => {
        const transaction = await getTransactionsByIds(form.systemId, tcodes);
        const newPermissionsTree = await getPfcgTreeForTcodes(form.systemId, tcodes);
        const defaultValues = await getDefaultPermissionsValuesForTransactions(form.systemId, tcodes);

        setForm((form) => {
            const menuChanges = produce({ menu: form.menu }, ({ menu }) => {
                addRoleMenuNode(activeNodePath, menu, tcodes, transaction);
            });

            const treeChanges = produce(
                { tree: form.tree, orgLevels: form.orgLevels, delta: form.delta, orgLevelsDelta: form.orgLevelsDelta },
                ({ tree, delta, orgLevels, orgLevelsDelta }) => {
                    for (const item of newPermissionsTree) {
                        addItemToRoleTree(form.profile, orgLevels, tree, item, null, 0, defaultValues, delta, orgLevelsDelta, {}, true);
                    }
                }
            );

            return {
                ...form,
                menu: menuChanges.menu,
                tree: treeChanges.tree,
                orgLevels: treeChanges.orgLevels,
                delta: treeChanges.delta,
                orgLevelsDelta: treeChanges.orgLevelsDelta,
            };
        });
    };
    const handleRemoveRoleMenuNode = (activeNodePath) => {
        const removedMenuItem = findItemInTree(activeNodePath, form.menu);
        const tcodesMapItem = getTcodesMapCountFromMenu([removedMenuItem]);

        const menuChanges = produce({ menu: form.menu }, ({ menu }) => {
            removeRoleMenuNode(activeNodePath, menu);
        });

        const tcodesMapMenu = getTcodesMapCountFromMenu(form.menu);
        const { deletedTcodes, remainedTcodes } = getDeletedTcodesFromMenu(tcodesMapItem, tcodesMapMenu);

        let updatedForm = { ...form, menu: menuChanges.menu };

        if (form.tree.length) {
            const { orgLevelsForDelete, profilesForDelete } = getDeletedDefaultValuesForTcodes(deletedTcodes, remainedTcodes, {}, Array.from(deletedTcodes));
            const treeChanges = produce(
                { tree: form.tree, delta: form.delta, orgLevels: form.orgLevels, orgLevelsDelta: form.orgLevelsDelta },
                ({ tree, delta, orgLevels, orgLevelsDelta }) => {
                    substractPfcgTreeFromRole(tree, delta, orgLevels, orgLevelsDelta, orgLevelsForDelete, profilesForDelete, Array.from(deletedTcodes));
                }
            );

            const newRoots = [];

            treeChanges.tree.forEach((node) => {
                if (node.children && node.children.length === 0) {
                    return;
                }

                newRoots.push(node);
            });

            updatedForm = {
                ...updatedForm,
                tree: newRoots,
                orgLevels: treeChanges.orgLevels,
                delta: treeChanges.delta,
                orgLevelsDelta: treeChanges.orgLevelsDelta,
            };
        }
        setForm(updatedForm);
    };

    const handleCopyRoleMenu = (checkedMenuItems) => {
        const changes = produce({ menu: form.menu }, ({ menu }) => {
            copyRoleMenu(checkedMenuItems, menu);
        });
        setForm({ ...form, menu: changes.menu });
    };

    const handleRemoveTreeItem = (path) => {
        const changes = produce({ tree: form.tree, delta: form.delta, orgLevels: form.orgLevels }, ({ tree, delta, orgLevels }) => {
            removeItemFromRoleTree(tree, path, delta, orgLevels);
        });
        setForm({ ...form, tree: changes.tree, delta: changes.delta, orgLevels: changes.orgLevels });
    };

    const handleExpandItemTree = (path) => {
        const changes = produce({ tree: form.tree, path: path }, ({ tree, path }) => {
            const item = findItemInTree(path, tree);
            item.open = !item.open;
            const treeWalker = new TreeWalker(item.children);
            treeWalker.walk(
                (node) => {
                    node.open = item.open;
                },
                () => {
                    return {
                        keyField: "",
                        keyValue: "",
                        childField: "children",
                    };
                },
                () => {}
            );
        });
        setForm({ ...form, tree: changes.tree });
    };

    return (
        <RoleModelingBox
            disabled={disabled}
            role={form}
            onAddTreeItems={handleAddTreeItems}
            onSetOrgLevelValues={handleSetOrgLevelValues}
            onTreeItemFieldChange={handleTreeItemFieldChange}
            onOpenRoleMenuNode={handleOpenRoleMenuNode}
            onAddRoleMenuFolder={handleAddRoleMenuFolder}
            onAddRoleMenuNode={handleAddRoleMenuNodeMass}
            onRemoveRoleMenuNode={handleRemoveRoleMenuNode}
            onCopyRoleMenu={handleCopyRoleMenu}
            onRemoveTreeItem={handleRemoveTreeItem}
            onExpandItemTree={handleExpandItemTree}
            searchString={searchString}
            setSearchString={setSearchString}
        />
    );
};

export default SingleRoleView;

SingleRoleView.propTypes = {
    form: PropTypes.object,
    setForm: PropTypes.func,
    setBusy: PropTypes.func,
    disabled: PropTypes.bool,
};
